import {
  Button,
  CheckboxGroup,
  FormFields,
  Heading,
  Input,
  OpenText,
  RadioGroup,
  mediumMediaBreakpoint,
  spacingL,
} from "@RooUI";
import { type InputRef } from "antd";
import React, { useEffect, useRef } from "react";
import style from "styled-components";

import { SubHeaderContainer } from "@Roo/Common/EditProfile/styles";
import { useRooTranslation } from "@Roo/Common/Wrappers/useRooTranslation";
import {
  CURRENT_SITUATION_OPTIONS,
  LOOKING_TO_START_OPTIONS,
  LOOKING_TO_START_USING_ROO,
  TYPICAL_AVAILABILITY,
} from "@Roo/constants/checkBoxConstants";

import { useFormContext } from "./RegistrationVetPreferencesFormContext";
import { ImprovedFiltersGroupWrapper } from "./styles";

const OPEN_RESPONSE_MIN_LENGTH = 10;
const OPEN_RESPONSE_MAX_LENGTH = 200;

export const ButtonContainer = style.div`
  display: flex;
  align-items: flex-end;
  height: 100%;
  margin-top: 32px;
  @media (max-width: ${mediumMediaBreakpoint}) {
    align-self: unset;
    width: 100%;
    margin-top: ${spacingL}
  }
`;

interface RegistrationVetPreferencesFormProps {
  highCommitment: boolean;
}

export const RegistrationVetPreferencesForm = ({
  highCommitment,
}: RegistrationVetPreferencesFormProps) => {
  const {
    onChange,
    onSave,
    loading,
    errors,
    form: {
      // @ts-expect-error TS2339
      yearsOfPractice,
      // @ts-expect-error TS2339
      preferredProcedures,
      // @ts-expect-error TS2339
      preferredAreasOfPractice,
      // @ts-expect-error TS2339
      typicalAvailableDays,
      // @ts-expect-error TS2339
      lookingToStart,
      // @ts-expect-error TS2339
      currentSituation,
      // @ts-expect-error TS2339
      typicalAvailableDaysOpenResponse,
      // @ts-expect-error TS2339
      lookingToStartOpenResponse,
    },
  } = useFormContext();

  const showLookingToStartTextbox = lookingToStart === LOOKING_TO_START_USING_ROO.OTHER;

  const showTypicalAvailableDaysTextbox = !!typicalAvailableDays.find(
    // @ts-expect-error TS7006
    (option) => option.id === TYPICAL_AVAILABILITY.VARIES && option.checked
  );

  const { t } = useRooTranslation();

  const yearsOfPracticeRef = useRef<InputRef>(null);
  const vetPreferencesTitle = t("registration.vetPreferences.splitCommitmentTitle");

  useEffect(() => {
    if (errors?.yearsOfPractice) {
      yearsOfPracticeRef.current?.input?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [errors?.yearsOfPractice]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!onChange) {
      return;
    }

    if (!showLookingToStartTextbox) {
      onChange("lookingToStartOpenResponse", undefined);
    }

    if (!showTypicalAvailableDaysTextbox) {
      onChange("typicalAvailableDaysOpenResponse", undefined);
    }
  }, [showLookingToStartTextbox, showTypicalAvailableDaysTextbox]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <SubHeaderContainer>
        <Heading level={2}>{vetPreferencesTitle}</Heading>
      </SubHeaderContainer>
      <FormFields>
        {highCommitment && (
          <Input
            label={t("registration.vetPreferences.form.yearsOfPracticeLabel")}
            type="number"
            value={yearsOfPractice}
            min="0"
            step="1"
            onChange={(e) => {
              const value = Math.floor(e.target.valueAsNumber);
              if (onChange) {
                onChange("yearsOfPractice", value);
              }
            }}
            error={errors?.["yearsOfPractice"]}
            id="yearsOfPractice"
            ref={yearsOfPracticeRef}
          />
        )}

        {/* Which of the below best describes your current work? */}
        {highCommitment && (
          <ImprovedFiltersGroupWrapper data-testid="currentSituation">
            <RadioGroup
              value={currentSituation}
              onChange={(value) => {
                if (onChange) {
                  onChange("currentSituation", parseInt(value));
                }
              }}
              radios={CURRENT_SITUATION_OPTIONS}
              label={t("registration.vetPreferences.form.currentSituationLabel")}
              error={errors?.["currentSituation"]}
              name="currentSituation"
              singleColumn={true}
            />
          </ImprovedFiltersGroupWrapper>
        )}

        {/* When are you looking to start taking shifts? */}
        <ImprovedFiltersGroupWrapper data-testid="lookingToStart">
          <RadioGroup
            value={lookingToStart}
            onChange={(value) => {
              if (onChange) {
                onChange("lookingToStart", parseInt(value));
              }
            }}
            radios={LOOKING_TO_START_OPTIONS}
            label={t("registration.vetPreferences.form.lookingToStartLabel")}
            error={errors?.["lookingToStart"]}
          />
          {showLookingToStartTextbox && (
            <OpenText
              placeholder={t("registration.vetPreferences.form.openResponsePlaceholder")}
              $error={errors?.["lookingToStartOpenResponse"]}
              minLength={OPEN_RESPONSE_MIN_LENGTH}
              maxLength={OPEN_RESPONSE_MAX_LENGTH}
              onChange={(e) => {
                if (onChange) {
                  onChange("lookingToStartOpenResponse", e.target.value);
                }
              }}
              value={lookingToStartOpenResponse}
              data-testid="lookingToStartInput"
            />
          )}
        </ImprovedFiltersGroupWrapper>

        {/* What types of shifts are you looking for? */}
        <ImprovedFiltersGroupWrapper>
          <CheckboxGroup
            label={t("registration.vetPreferences.form.preferredAreasOfPracticeLabel")}
            onChange={(_e, updatedCheckboxes) => {
              if (onChange) {
                onChange("preferredAreasOfPractice", updatedCheckboxes);
              }
            }}
            checkboxes={preferredAreasOfPractice}
            error={errors?.["preferredAreasOfPractice"]}
            name="preferredAreasOfPractice"
          />
        </ImprovedFiltersGroupWrapper>

        {/* What types of procedures are you comfortable doing on a relief shift? */}
        <ImprovedFiltersGroupWrapper>
          <CheckboxGroup
            label={t("registration.vetPreferences.form.preferredProceduresLabel")}
            onChange={(_e, updated) => {
              if (onChange) {
                onChange("preferredProcedures", updated);
              }
            }}
            checkboxes={preferredProcedures}
            error={errors?.["preferredProcedures"]}
            name="preferredProcedures"
          />
        </ImprovedFiltersGroupWrapper>

        {/* What days are you typically available for relief shifts? */}
        {highCommitment && (
          <ImprovedFiltersGroupWrapper data-testid="typicalAvailableDays">
            <CheckboxGroup
              label={t("registration.vetPreferences.form.typicalAvailableDaysLabel")}
              onChange={(_e, updated) => {
                if (onChange) {
                  // @ts-expect-error TS2345
                  onChange("typicalAvailableDays", updated);
                }
              }}
              checkboxes={typicalAvailableDays}
              error={errors?.["typicalAvailableDays"]}
              name="typicalAvailableDays"
            />
            {showTypicalAvailableDaysTextbox && (
              <OpenText
                placeholder={t("registration.vetPreferences.form.openResponsePlaceholder")}
                $error={errors?.["typicalAvailableDaysOpenResponse"]}
                minLength={OPEN_RESPONSE_MIN_LENGTH}
                maxLength={OPEN_RESPONSE_MAX_LENGTH}
                onChange={(e) => {
                  if (onChange) {
                    onChange("typicalAvailableDaysOpenResponse", e.target.value);
                  }
                }}
                value={typicalAvailableDaysOpenResponse}
                data-testid="typicalAvailableDaysInput"
              />
            )}
          </ImprovedFiltersGroupWrapper>
        )}
      </FormFields>
      <ButtonContainer>
        <Button
          $buttonType="primary"
          title="Continue"
          onClick={onSave}
          loading={loading}
          data-testid="vetPreferencesContinue"
        />
      </ButtonContainer>
    </>
  );
};
